import { useQuery } from '@tanstack/react-query';

import { TeamRole, TeamMembership } from '@/react/portainer/users/teams/types';
import { useCurrentUser, useIsEdgeAdmin } from '@/react/hooks/useUser';

import { UserId } from './types';
import { getUserMemberships } from './user.service';
import { userQueryKeys } from './queries/queryKeys';

interface UseUserMembershipOptions<TSelect> {
  select?(userMemberships: TeamMembership[]): TSelect;
  enabled?: boolean;
}

export function useUserMembership<TSelect = TeamMembership[]>(
  userId: UserId,
  { enabled, select }: UseUserMembershipOptions<TSelect> = {}
) {
  return useQuery(
    userQueryKeys.memberships(userId),
    ({ signal }) => getUserMemberships(userId, { signal }),
    { select, enabled }
  );
}

export function useIsCurrentUserTeamLeader() {
  const { user } = useCurrentUser();
  const isAdminQuery = useIsEdgeAdmin();

  const query = useUserMembership(user.Id, {
    enabled: !isAdminQuery.isLoading && !isAdminQuery.isAdmin,
    select: (memberships) =>
      memberships.some((membership) => membership.Role === TeamRole.Leader),
  });

  if (isAdminQuery.isLoading) {
    return false;
  }

  return isAdminQuery.isAdmin ? true : !!query.data;
}
